import { asList } from '../lib/api';
import { useEffect, useRef, useState } from 'react';
import { assistantApi } from '../lib/assistantApi';
import type { AssistantAnswer, ThreadSummary, TurnView } from '../lib/assistantApi';

/**
 * ⚠ Plain English, not the tool name with its underscores stripped. "from your worst collecting
 * classes records" is what that produced, and it reads like a database table talking to you.
 */
function sourceLabel(tool: string): string {
  const map: Record<string, string> = {
    school_overview: 'from your student, fee and attendance records',
    fees_outstanding: 'from your invoice and payment ledger',
    worst_collecting_classes: 'from your invoice and payment ledger, by class',
    class_fees: 'from that class\'s invoices and payments',
    lowest_attendance_classes: 'from your attendance register, by class',
    class_attendance: 'from that class\'s attendance register',
    class_toppers: 'from finalized report cards',
  };
  return map[tool] ?? 'from your school records';
}

/**
 * ASK EDUVORA — conversational questions about the school, with saved history.
 *
 * ⚠ THE FACTS ARE RENDERED BESIDE THE PROSE, ALWAYS. That is the core design rule of this screen,
 * not decoration.
 *
 * The server computes every figure in C# and the model is only allowed to write sentences around
 * them. Printing those figures next to the answer means a hallucinated number would visibly
 * contradict the evidence directly beneath it — so the guarantee is not merely asserted in a
 * marketing line, it is checkable by the person reading it, in the moment, without leaving the page.
 *
 * ⚠ A templated answer is NOT an error state. When AI is off or OpenAI fails, the server falls back
 * to deterministic prose over the same figures. The chip says "computed" instead of "AI", and that is
 * the whole difference. Never render the fallback as a failure — it is a correct answer.
 */
export function AskEduvoraPage() {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [turns, setTurns] = useState<TurnView[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastAiUsed, setLastAiUsed] = useState<boolean | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  /**
   * ⚠ THE SHELL IS MEASURED, NOT GUESSED.
   *
   * This started as `height: calc(100vh - 132px)` in CSS — a hardcoded allowance for the app header.
   * The header is not 132px at every breakpoint, and the page padding changes at 900px and 640px, so
   * the shell overflowed and **pushed the composer below the fold**: a chat screen you cannot type
   * into. Measuring the real distance from the top of the viewport is immune to every one of those.
   */
  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const fit = () => {
      const top = el.getBoundingClientRect().top;
      el.style.height = `${Math.max(420, window.innerHeight - top - 16)}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  useEffect(() => {
    assistantApi.threads().then((r) => setThreads(asList(r))).catch(() => setThreads([]));
    assistantApi.suggestions().then((r) => setSuggestions(r.suggestions)).catch(() => setSuggestions([]));
  }, []);

  // Keep the newest exchange in view as the conversation grows.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, asking]);

  async function openThread(id: string) {
    setSidebarOpen(false);
    setError(null);
    try {
      const t = await assistantApi.thread(id);
      setThreadId(t.id);
      setTurns(t.turns);
    } catch {
      setError('That conversation could not be opened.');
    }
  }

  function newThread() {
    setThreadId(null);
    setTurns([]);
    setError(null);
    setSidebarOpen(false);
    inputRef.current?.focus();
  }

  async function ask(text: string) {
    const q = text.trim();
    if (!q || asking) return;

    setAsking(true);
    setError(null);
    setQuestion('');

    // ⚠ The question is shown IMMEDIATELY, before the server answers. Waiting for the round-trip
    // makes the app feel broken on a slow connection — the user cannot tell whether their question
    // was even received.
    const pending: TurnView = {
      ordinal: turns.length + 1,
      question: q,
      answer: '',
      toolName: null,
      facts: [],
      rows: [],
      grounded: true,
      askedAt: new Date().toISOString(),
    };
    setTurns((t) => [...t, pending]);

    try {
      const res: AssistantAnswer = await assistantApi.ask(q, threadId);
      setThreadId(res.threadId);
      setLastAiUsed(res.aiUsed);
      setTurns((t) => [...t.slice(0, -1), res.turn]);

      // Refresh the sidebar so a brand-new conversation appears in history straight away.
      assistantApi.threads().then((r) => setThreads(asList(r))).catch(() => {});
    } catch {
      setTurns((t) => t.slice(0, -1));
      setError('I could not reach the assistant just now. Please try again.');
      setQuestion(q); // give them their typing back rather than losing it
    } finally {
      setAsking(false);
    }
  }

  async function removeThread(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await assistantApi.remove(id);
      setThreads((t) => t.filter((x) => x.id !== id));
      if (id === threadId) newThread();
    } catch {
      /* leave the row in place — a delete that silently vanishes is worse than one that fails */
    }
  }

  return (
    <div className="ask-shell" ref={shellRef}>
      {/* ---------------- history ---------------- */}
      <aside className={'ask-side' + (sidebarOpen ? ' is-open' : '')}>
        <button type="button" className="ask-new" onClick={newThread}>
          + New conversation
        </button>

        <div className="ask-side-label">Recent</div>

        {threads.length === 0 && (
          <p className="ask-empty-side">Your conversations will be saved here.</p>
        )}

        {threads.map((t) => (
          <div
            key={t.id}
            className={'ask-thread' + (t.id === threadId ? ' is-on' : '')}
            onClick={() => openThread(t.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openThread(t.id);
              }
            }}
          >
            <div className="ask-thread-title">{t.title}</div>
            <div className="ask-thread-meta">
              {t.turns} {t.turns === 1 ? 'question' : 'questions'}
            </div>
            <button
              type="button"
              className="ask-del"
              aria-label={`Delete conversation: ${t.title}`}
              onClick={(e) => removeThread(t.id, e)}
            >
              ×
            </button>
          </div>
        ))}
      </aside>

      {/* ---------------- conversation ---------------- */}
      <section className="ask-main">
        <div className="ask-head">
          <button
            type="button"
            className="ask-burger"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Show saved conversations"
          >
            ☰
          </button>
          <div>
            <div className="eyebrow">Ask Eduvora</div>
            <h1 className="h-display" style={{ fontSize: 22, margin: 0 }}>
              Ask about your school
            </h1>
          </div>
          {lastAiUsed === false && (
            <span className="pill pill-muted ask-mode" title="AI is unavailable — the figures are unchanged, only the wording is templated.">
              computed
            </span>
          )}
        </div>

        <div className="ask-scroll">
          {turns.length === 0 && (
            <div className="ask-welcome">
              <p className="ask-welcome-lede">
                Every answer is calculated from your own records — the same figures as the dashboard.
                The workings are shown under each answer.
              </p>
              {suggestions.length > 0 && (
                <>
                  <div className="ask-side-label" style={{ marginTop: 20 }}>Try asking</div>
                  <div className="ask-suggest">
                    {suggestions.map((s) => (
                      <button key={s} type="button" className="chip" onClick={() => ask(s)}>
                        {s}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {turns.map((t) => (
            <div key={t.ordinal} className="ask-turn">
              <div className="ask-q">{t.question}</div>

              {t.answer === '' ? (
                <div className="ask-a ask-thinking">
                  <span className="ask-dot" />
                  <span className="ask-dot" />
                  <span className="ask-dot" />
                  <span style={{ marginLeft: 6 }}>Checking the records…</span>
                </div>
              ) : (
                <div className="ask-a">
                  <p className={'ask-prose' + (t.grounded ? '' : ' is-soft')}>{t.answer}</p>

                  {/* ⚠ THE EVIDENCE. Shown for every grounded answer, never collapsed by default —
                      the moment it is hidden behind a toggle, nobody checks it and the guarantee
                      quietly becomes a claim. */}
                  {t.facts.length > 0 && (
                    <div className="ask-facts">
                      {t.facts.map((f) => (
                        <div className="ask-fact" key={f.label}>
                          <span className="ask-fact-l">{f.label}</span>
                          <span className="ask-fact-v">{f.value}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {t.rows.length > 0 && (
                    <table className="ask-table">
                      <tbody>
                        {t.rows.map((r) => (
                          <tr key={r.label}>
                            <td>{r.label}</td>
                            <td className="num">{r.value}</td>
                            <td className="detail">{r.detail}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {t.toolName && <div className="ask-source">{sourceLabel(t.toolName)}</div>}
                </div>
              )}
            </div>
          ))}

          {error && <div className="ask-error">{error}</div>}
          <div ref={endRef} />
        </div>

        {/* ---------------- composer ---------------- */}
        <div className="ask-composer">
          <textarea
            ref={inputRef}
            className="ask-input"
            rows={1}
            placeholder="Ask about fees, attendance or results…"
            value={question}
            disabled={asking}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter is a newline. Standard, and what everyone expects.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                ask(question);
              }
            }}
          />
          <button
            type="button"
            className="ask-send"
            disabled={asking || question.trim().length === 0}
            onClick={() => ask(question)}
          >
            {asking ? '…' : 'Ask'}
          </button>
        </div>
      </section>
    </div>
  );
}
